<template>
  <div>
    <h3 class="mb-100">{{ $t('service.headline.certifications') }}</h3>

    <LcCard>
      <ul>
        <li
          v-for="{ alt, content, link, src, title } in items"
          :key="title"
          class="trust-item flex items-center py-200 px-none"
        >
          <HyperLink v-if="link" class="trust-item-link mr-200" :to="link">
            <NuxtImg class="trust-item-img" v-bind="{ alt, src }" />
          </HyperLink>

          <NuxtImg v-else class="trust-item-img mr-200" v-bind="{ alt, src }" />

          <div class="paragraph-small-text">
            <ContentfulRenderer v-if="content?.json" :data="content" />
            <p v-else>
              {{ content }}
            </p>
          </div>
        </li>
      </ul>
    </LcCard>
  </div>
</template>

<script lang="ts" setup>
import type { TrustCertificates } from '#content/shared/@types/generated/cms/graphql-schema-types'

const { data } = useContent().getTrustCertificates()

// Editors control the section order via a "[1] " style prefix in the title
const stripOrderPrefix = (title: string) => title.replace(/^\[\d+\]\s*/, '')

const items = computed(() =>
  data.value?.items
    ? R_sortBy(
        data.value.items.map((item: Maybe<TrustCertificates>) => {
          const link = item?.link ?? null
          const title = item?.title || ''

          return {
            // inside a link the title would pollute the link's accessible name,
            // which the description already provides
            alt: link ? '' : stripOrderPrefix(title),
            content: item?.description,
            link,
            src: item?.certificate?.url ?? '',
            title,
          }
        }),
        ({ title }) => title,
      )
    : [],
)
</script>

<style lang="scss" scoped>
.trust-item {
  border-top: separator('line');

  &:first-child {
    padding-top: 0;
    border-top: none;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &-link {
    position: relative;
    flex-shrink: 0;
    cursor: pointer;

    @include focus-state {
      box-shadow: 0 0 0 token('size/border/200') token('color/border/accent/focused/focused');
    }
  }

  &-img {
    // without this a long description squeezes the logo on small viewports
    flex-shrink: 0;
    width: 6rem;
  }
}
</style>
